<script setup lang="ts">
import { computed } from 'vue'
import { ChevronDown, Globe, LogOut, Mail, User } from 'lucide-vue-next'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'radix-vue'
import { appConfig } from '@/config/appConfig'
import { performLogout } from '@/composables/useAuth'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

const username = computed(() => authStore.username || 'Operator')
const email = computed(() => authStore.email || '')
const role = computed(() => authStore.operatorRole || 'Operator')
const initial = computed(() => (authStore.username || 'O').charAt(0).toUpperCase())

type EnvMeta = { label: string; classes: string }
const ENV_META: Record<'dev' | 'stg' | 'prd', EnvMeta> = {
  dev: { label: 'DEV', classes: 'bg-status-info-focused text-status-info' },
  stg: { label: 'STAGING', classes: 'bg-status-warning-focused text-status-warning' },
  prd: { label: 'PRODUCTION', classes: 'bg-status-danger-focused text-status-danger' },
}
const env = computed(() => ENV_META[appConfig.appEnv()])
</script>

<template>
  <header
    class="flex h-header shrink-0 items-center justify-between gap-l border-b border-border-primary bg-background-light px-3xl"
  >
    <div class="flex min-w-0 items-center gap-s">
      <span
        class="flex items-center gap-xs rounded-full border border-primary-disabled bg-primary-accent px-s py-xxs text-body-extra-small-medium text-text-brand-primary"
      >
        <Globe :size="14" :stroke-width="2.25" class="shrink-0" />
        Operator console — all tenants visible
      </span>
      <span
        class="shrink-0 rounded-md px-xs py-px text-super-small font-semibold uppercase tracking-wider"
        :class="env.classes"
      >
        {{ env.label }}
      </span>
    </div>

    <div class="flex shrink-0 items-center gap-m">
      <DropdownMenuRoot>
        <DropdownMenuTrigger
          class="group flex items-center gap-s rounded-md py-xxs pl-m pr-xs transition-colors hover:bg-background-gray-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-focused"
          aria-label="Open operator menu"
        >
          <div class="flex min-w-0 flex-col items-end leading-tight">
            <span class="truncate text-body-small-medium text-text-title">{{ username }}</span>
            <span class="truncate text-body-extra-small text-text-subtitle">
              {{ email || role }}
            </span>
          </div>
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-enable text-body-small-medium text-text-inverted"
          >
            {{ initial }}
          </span>
          <ChevronDown
            :size="16"
            class="shrink-0 text-icon-secondary transition-transform group-data-[state=open]:rotate-180"
          />
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
          <DropdownMenuContent
            align="end"
            :side-offset="8"
            class="z-50 min-w-[256px] overflow-hidden rounded-lg border border-border-primary bg-background-light shadow-4 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          >
            <DropdownMenuLabel class="flex items-center gap-s px-m py-s">
              <span
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-enable text-body-small-medium text-text-inverted"
              >
                {{ initial }}
              </span>
              <div class="flex min-w-0 flex-col leading-tight">
                <span class="truncate text-body-small-medium text-text-title">{{ username }}</span>
                <span class="truncate text-body-extra-small text-text-subtitle">{{ email }}</span>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator class="h-px bg-border-primary" />

            <div class="flex flex-col gap-2xxs px-m py-s">
              <div class="flex items-center gap-xs text-body-extra-small text-text-subtitle">
                <User :size="14" class="shrink-0 text-icon-secondary" />
                <span class="truncate">{{ role }}</span>
              </div>
              <div
                v-if="email"
                class="flex items-center gap-xs text-body-extra-small text-text-subtitle"
              >
                <Mail :size="14" class="shrink-0 text-icon-secondary" />
                <span class="truncate">{{ email }}</span>
              </div>
            </div>

            <DropdownMenuSeparator class="h-px bg-border-primary" />

            <DropdownMenuItem
              class="flex cursor-pointer items-center gap-s px-m py-s text-body-small-medium text-status-danger transition-colors hover:bg-status-danger-focused focus:bg-status-danger-focused focus:outline-none"
              @select="performLogout"
            >
              <LogOut :size="18" class="shrink-0" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenuRoot>
    </div>
  </header>
</template>
